// src/components/crm/SnoozeQuoteModal.js
// SEAM 7 VERIFIER: qsnooze-9f4c2e18-modal
//
// "Snooze" capture on the operator quote detail page. Two inputs: the date to
// snooze until (required, and must be in the future) and a reason (optional).
//
// WHAT A SNOOZE IS (Quote Lifecycle Addendum 4, A4.1): moving the follow-up date
// plus a suppression flag — the Close CRM model. It is NOT a pipeline stage and
// NOT a calendar entry of its own. So snoozing to a date also MOVES the next
// follow-up to that date: there is only ever one date on a quote, never two.
//
// THE REASON IS THE POINT, not decoration. It is what the salesperson needs a
// week later — "client asked to call back after their reno" — so it is offered
// every time rather than buried behind a "add a note" affordance.
//
// INTERNAL ONLY: never mount this in a portal view.
//
// Writes go through the setQuoteSnooze Cloud Function — firestore.rules denies
// client writes to snoozedUntil, snoozeReason and followUpHasTime along with
// nextFollowUpAt and followUpCount, as one group. The page's live quote
// listener delivers the change afterwards.
import React, { useState } from 'react';
import { httpsCallable } from 'firebase/functions';
import { X, BellOff } from 'lucide-react';
import { functions } from '../../firebase/config';

// Matches SNOOZE_REASON_MAX_LENGTH in functions/utils/quoteFollowUp.js. The
// server is authoritative; this only stops someone typing past the limit and
// being refused after the fact.
const REASON_MAX_LENGTH = 200;

const SnoozeQuoteModal = ({ isOpen, onClose, quote, docWord = 'Estimate' }) => {
    const [until, setUntil] = useState('');
    const [untilTime, setUntilTime] = useState('');
    const [reason, setReason] = useState('');
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState('');

    if (!isOpen || !quote?.id) return null;

    const reset = () => {
        setUntil(''); setUntilTime(''); setReason('');
        setError(''); setSaving(false);
    };

    const handleClose = () => { if (!saving) { reset(); onClose(); } };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (saving) return;                       // single-fire guard
        if (!until) {
            setError('Choose the date to snooze until.');
            return;
        }
        setSaving(true);
        setError('');
        try {
            await httpsCallable(functions, 'setQuoteSnooze')({
                quoteId: quote.id,
                // Same optional-time rule as Log follow-up: with a time, send
                // that instant and say so; without one, midday local so a
                // date-only intention can't slide a day west of UTC.
                until: new Date(`${until}T${untilTime || '12:00'}:00`).toISOString(),
                untilHasTime: !!untilTime,
                reason: reason.trim() || null,
            });
            reset();
            onClose();
        } catch (err) {
            console.error('SnoozeQuoteModal: snooze failed', err);
            setError(err?.message || 'Could not snooze this estimate. Please try again.');
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
                <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
                    <h3 className="flex items-center gap-2 text-lg font-semibold text-gray-800">
                        <BellOff size={18} className="text-[#5F4EC9]" /> Snooze {docWord.toLowerCase()}
                    </h3>
                    <button type="button" onClick={handleClose} disabled={saving}
                        className="text-gray-400 hover:text-gray-600 disabled:opacity-50">
                        <X size={20} />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="px-5 py-4 space-y-4">
                    {/* qsnooze-9f4c2e18-explain — founder test, 2026-09-27: "Snooze is
                        not understandable in the UI". Says what it DOES, up front,
                        before the inputs. Replaces the footer note that used to carry
                        the same facts after the buttons, where it read as fine print. */}
                    <p className="text-sm text-gray-600">
                        Pauses the automatic follow-up reminders and moves the next follow-up to
                        this date. Expiry warnings still apply.
                    </p>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Snooze until <span className="text-red-500">*</span>
                        </label>
                        <div className="flex gap-2">
                            <input
                                type="date"
                                value={until}
                                onChange={(e) => setUntil(e.target.value)}
                                disabled={saving}
                                className="flex-1 min-w-0 px-3 py-2 text-sm border border-gray-200 rounded-lg disabled:opacity-60"
                            />
                            <input
                                type="time"
                                value={untilTime}
                                onChange={(e) => setUntilTime(e.target.value)}
                                disabled={saving || !until}
                                aria-label="Time of day (optional)"
                                title={until ? 'Time of day (optional)' : 'Pick a date first'}
                                className="w-28 shrink-0 px-3 py-2 text-sm border border-gray-200 rounded-lg disabled:opacity-60 disabled:bg-gray-50"
                            />
                        </div>
                        <p className="mt-1 text-xs text-gray-400">
                            Leave the time blank for &ldquo;sometime that day&rdquo;.
                        </p>
                    </div>

                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Why? <span className="text-gray-400 font-normal">(optional)</span>
                        </label>
                        <input
                            type="text"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            disabled={saving}
                            maxLength={REASON_MAX_LENGTH}
                            placeholder="e.g. client asked to call back after their reno"
                            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg disabled:opacity-60"
                        />
                        <p className="mt-1 text-xs text-gray-400">
                            Shown on the {docWord.toLowerCase()} while it&apos;s snoozed, so you know why when it wakes up.
                        </p>
                    </div>

                    {error && <p className="text-sm text-red-600">{error}</p>}

                    <div className="flex justify-end gap-2 pt-1">
                        <button type="button" onClick={handleClose} disabled={saving}
                            className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50">
                            Cancel
                        </button>
                        <button type="submit" disabled={saving}
                            className="px-4 py-2 text-sm font-medium text-white bg-[#5F4EC9] rounded-lg hover:bg-[#4f40a8] disabled:opacity-50">
                            {saving ? 'Saving…' : 'Snooze'}
                        </button>
                    </div>
                    <p className="text-xs text-gray-400 pt-1">
                        Internal: the customer is never told, and nothing is sent to them.
                    </p>
                </form>
            </div>
        </div>
    );
};

export default SnoozeQuoteModal;
